import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getAcToken, setAcToken, setRfToken } from "../../utils/local-storage";
import {
  loginUser,
  verifyCode,
  forgotPassword,
  resetPassword,
  getUserInfor,
  updateUserInfor,
  resendVerificationCode,
  switchCompany,
} from "../actions/auth.action";

// Types
export interface User {
  _id: string;
  company_id: string;
  full_name: string;
  email: string;
  is_super_admin: boolean;
  role: number;
  token: string;
  refresh_token: string;
  language: string;
  lastActive: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isRequestedLogin: boolean;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem("user") || "null"),
  isAuthenticated: !!getAcToken(),
  isRequestedLogin: false,
  loading: false,
  error: null,
};

// Auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      localStorage.clear();
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload.data.token) {
          state.isAuthenticated = true;
          setAcToken(action.payload.data.token);
          setRfToken(action.payload.data.refresh_token);
          state.user = action.payload.data;
          localStorage.setItem("user", JSON.stringify(action.payload.data));
        } else {
          state.isRequestedLogin = true;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(verifyCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyCode.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.isAuthenticated = true;
        setAcToken(action.payload.data.token);
        setRfToken(action.payload.data.refresh_token);
        state.user = action.payload.data;
        localStorage.setItem("user", JSON.stringify(action.payload.data));
      })
      .addCase(verifyCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(getUserInfor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserInfor.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload.data;
        localStorage.setItem("user", JSON.stringify(action.payload.data));
      })
      .addCase(getUserInfor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(updateUserInfor.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserInfor.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateUserInfor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(resendVerificationCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendVerificationCode.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resendVerificationCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(switchCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(switchCompany.fulfilled, (state, action) => {
        state.loading = false;
        setAcToken(action.payload.data.token);
        setRfToken(action.payload.data.refresh_token);
        state.user = action.payload.data;
        localStorage.setItem("user", JSON.stringify(action.payload.data));
        state.error = null;
      })
      .addCase(switchCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, logout } = authSlice.actions;
export default authSlice.reducer;
